import { lazy, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FournisseurPeriode } from './periode.tsx'
import { configure, supabase } from './supabase.ts'
import { espace } from './requetes.ts'
import { FournisseurSession, useSession, Connexion } from './auth.tsx'
import { Coque } from './Coque.tsx'
const TableauDeBord = lazy(() => import('../design/TableauDeBord.tsx'))
const ATraiter = lazy(() => import('../design/ATraiter.tsx'))
const Planche = lazy(() => import('../design/Planche.tsx'))
import Bientot from '../design/Bientot.tsx'
const Revenus = lazy(() => import('../design/Revenus.tsx'))
const Acquisition = lazy(() => import('../design/Acquisition.tsx'))
const Equipe = lazy(() => import('../design/Equipe.tsx'))
const FicheVa = lazy(() => import('../design/FicheVa.tsx'))
const Geelark = lazy(() => import('../design/Geelark.tsx'))
const AnalyticsVa = lazy(() => import('../design/AnalyticsVa.tsx'))
const Sante = lazy(() => import('../design/Sante.tsx'))
const Automatisations = lazy(() => import('../design/Automatisations.tsx'))
const Chatting = lazy(() => import('../design/Chatting.tsx'))
const ContentFabrique = lazy(() => import('../design/Farm.tsx').then((m) => ({ default: m.ContentFabrique })))
const FarmFleet = lazy(() => import('../design/Farm.tsx').then((m) => ({ default: m.FarmFleet })))
const FarmModel = lazy(() => import('../design/Farm.tsx').then((m) => ({ default: m.FarmModel })))
const FarmPlanning = lazy(() => import('../design/Farm.tsx').then((m) => ({ default: m.FarmPlanning })))
const FarmSante = lazy(() => import('../design/FarmSante.tsx').then((m) => ({ default: m.FarmSante })))
const FarmComptes = lazy(() => import('../design/FarmSante.tsx').then((m) => ({ default: m.FarmComptes })))
const FarmAnalytics = lazy(() => import('../design/FarmAnalytics.tsx').then((m) => ({ default: m.FarmAnalytics })))
const Liens = lazy(() => import('../design/Liens.tsx'))
const EspaceVa = lazy(() => import('../design/EspaceReddit.tsx'))
const EcranManageuse = lazy(() => import('../design/EspaceManageuse.tsx'))
const Manageuses = lazy(() => import('../design/Manageuses.tsx'))
const Captions = lazy(() => import('../design/Captions.tsx'))

const Chargement = <div className="ds"><div className="ds-contenu"><p className="ds-discret">Ouverture…</p></div></div>

function Garde({ children }: { children: React.ReactNode }) {
  const { session, pret } = useSession()
  // Qui est connecte decide de l ecran : la direction a le dash, la VA Reddit son espace.
  const qui = useQuery({ queryKey: ['espace', session?.user.id], queryFn: espace, enabled: configure && !!session, staleTime: 10 * 60e3 })
  if (!configure) return <>{children}</>
  if (!pret) return null
  if (!session) return <div className="ds"><Connexion /></div>
  if (qui.isLoading) return null
  if (qui.data?.espace === 'reddit') return <Suspense fallback={Chargement}><EspaceVa email={session.user.email ?? null} deconnecter={() => supabase?.auth.signOut()} /></Suspense>
  if (qui.data?.espace === 'manageuse') {
    return (
      <FournisseurPeriode>
        <Suspense fallback={Chargement}><EcranManageuse email={session.user.email ?? null} deconnecter={() => supabase?.auth.signOut()} /></Suspense>
      </FournisseurPeriode>
    )
  }
  if (qui.data && qui.data.espace !== 'direction') {
    return (
      <div className="ds"><div className="connexion"><div className="connexion__carte">
        <div className="coque__marque">MUSE</div>
        <p>Ton accès n’ouvre aucun écran pour l’instant. Demande à la direction.</p>
        <button onClick={() => supabase?.auth.signOut()}>Se déconnecter</button>
      </div></div></div>
    )
  }
  return <>{children}</>
}

export function App() {
  return (
    <FournisseurSession>
    <Garde>
    <FournisseurPeriode>
      <Coque>
        <Suspense fallback={<div className="ds-carte"><p className="ds-discret" style={{ margin: 0 }}>Ouverture…</p></div>}>
        <Routes>
          <Route path="/" element={<Navigate to="/tableau-de-bord" replace />} />
          <Route path="/aujourdhui" element={<Navigate to="/tableau-de-bord" replace />} />
          <Route path="/tableau-de-bord" element={<TableauDeBord />} />
          <Route path="/a-traiter" element={<ATraiter />} />
          <Route path="/portfolio" element={<Navigate to="/farm/model" replace />} />
          <Route path="/farm/fleet" element={<FarmFleet />} />
          <Route path="/farm/planning" element={<FarmPlanning />} />
          <Route path="/farm/comptes" element={<FarmComptes />} />
          <Route path="/farm/sante" element={<FarmSante />} />
          <Route path="/farm/analytics" element={<FarmAnalytics />} />
          <Route path="/farm/model" element={<FarmModel />} />
          <Route path="/content/fabrique" element={<ContentFabrique />} />
          <Route path="/geelark/regie" element={<Navigate to="/farm/fleet" replace />} />
          <Route path="/revenue" element={<Revenus />} />
          <Route path="/acquisition" element={<Acquisition />} />
          <Route path="/acquisition/:vue" element={<Acquisition />} />
          <Route path="/equipe" element={<Equipe />} />
          <Route path="/equipe/:id" element={<FicheVa />} />
          <Route path="/analytics-va" element={<AnalyticsVa />} />
          <Route path="/manageuses" element={<Manageuses />} />
          <Route path="/espace-manageuse" element={<Navigate to="/manageuses" replace />} />
          <Route path="/chatting" element={<Chatting />} />
          <Route path="/systeme" element={<Sante />} />
          <Route path="/automatisations" element={<Automatisations />} />
          <Route path="/design" element={<Planche />} />
          <Route path="/geelark" element={<Geelark />} />
          <Route path="/geelark/telephone/:id" element={<Geelark />} />
          <Route path="/geelark/:vue" element={<Geelark />} />
          <Route path="/reddit/*" element={<Navigate to="/tableau-de-bord" replace />} />
          <Route path="/liens" element={<Liens />} />
          <Route path="/captions" element={<Captions />} />
          <Route path="*" element={<Bientot titre="Écran inconnu" texte="Cette adresse ne correspond à aucun écran." etape="Revenir à l’accueil depuis le menu." />} />
        </Routes>
        </Suspense>
      </Coque>
    </FournisseurPeriode>
    </Garde>
    </FournisseurSession>
  )
}
